import { Typography } from "@mui/material";
import { OLButton } from "OLButton";
import { OLPaper } from "OLPaper";
import { useExports } from "api/exports";
import { hasModifyAccessToResource } from "api/general";
import { ReactComponent as Expand04Icon } from "components/Icons/expand-04.svg";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { OLTabs } from "components/Tabs/OLTabs";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { useMessage } from "hooks/intl";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExportType } from "types/exports";
import { TierDetailsType } from "types/tierSets";
import { links } from "utilities/links";
import { BasicSettings } from "./BasicSettings";
import { Benefits } from "./Benefits";
import { Tier } from "./Tier";
import { useStyles } from "./styles";

interface Props {
  tiers: TierDetailsType[];
}

export const TierSetTiers = ({ tiers }: Props) => {
  const [activeTab, setActiveTab] = useState(tiers?.[0].levelId);
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [isExpandedView, setExpandedView] = useState(false);
  const classes = useStyles();
  const { notify } = useNotifications();
  const { message } = useMessage();
  const { exportList } = useExports();
  const navigate = useNavigate();
  const selectedTier = tiers.find((tier) => tier.levelId === activeTab);
  const activeBenefits = selectedTier?.rewards
    ? selectedTier.rewards.filter((benefit) => benefit.active)
    : [];

  const hasModifyExportPermission = hasModifyAccessToResource("EXPORT");

  const areBenefits = activeBenefits.length > 0;

  const handleExportList = async () => {
    const data = await exportList(ExportType.MEMBER_TIER, {
      memberTier: {
        tierId: selectedTier?.levelId,
      },
    });
    if (data) {
      notify(message({ id: "exportSnackbarInfo" }), {
        button: {
          text: message({ id: "viewList" }),
          onClick: ({ closeNotification }) => {
            closeNotification();
            return navigate(links.importsExports, {
              state: { activeTab: "exports" },
            });
          },
        },
      });
      setConfirmationModalOpen(false);
    }
  };

  return (
    <>
      <OLPaper noPadding className={classes.tierList}>
        <OLTabs
          items={tiers?.map((tier) => ({
            id: tier.levelId,
            name: tier.name,
          }))}
          value={activeTab}
          maxLabelLength={12}
          onChange={(_e, tab: string) => setActiveTab(tab)}
          className={classes.tabs}
        />
        <div className={classes.separator} />
        {hasModifyExportPermission && (
          <OLButton
            className={classes.exportBtn}
            onClick={() => setConfirmationModalOpen(true)}
            data-test-id="export-to-csv"
          >
            {message({ id: "exportToCSV" })}
          </OLButton>
        )}
        <BasicSettings
          name={selectedTier?.name || "-"}
          description={selectedTier?.description || "-"}
          isActive={!!selectedTier?.active}
        />
        {areBenefits && <Benefits rewards={activeBenefits} />}
      </OLPaper>
      <div className={classes.conditionsHeader}>
        <Typography mt={12} className={classes.title}>
          {message({ id: "tiersConditions" })}
        </Typography>
        <OLButton
          onClick={() => setExpandedView(true)}
          className={classes.expandIcon}
          startIcon={<Expand04Icon />}
        >
          {message({ id: "expandMore" })}
        </OLButton>
      </div>
      <OLPaper
        className={`${classes.conditionList} ${
          isExpandedView ? classes.expandedView : ""
        }`}
      >
        {isExpandedView && (
          <div className={classes.conditionsHeader}>
            <Typography className={classes.title}>
              {message({ id: "tiersConditions" })}
            </Typography>
            <span className={classes.closeIconWrapper}>
              <XCloseIcon onClick={() => setExpandedView(false)} />
            </span>
          </div>
        )}
        <div className={classes.tiers}>
          {tiers.map((tier, index) => (
            <Tier tier={tier} key={index} isExpandedView={isExpandedView} />
          ))}
        </div>
      </OLPaper>
      <ConfirmationModal
        action={() => handleExportList()}
        close={() => setConfirmationModalOpen(false)}
        header={message({ id: "exportConfirmation" })}
        info=""
        open={isConfirmationModalOpen}
        cancelText={message({ id: "cancel" })}
        submitText={message({ id: "export" })}
        data-test-id="export-modal"
        modalWidth={454}
      />
    </>
  );
};
